<template>
  <div
    class="bubble-toggle-group"
    data-tour-id="samples-register-bubble"
  >
    <button
      v-if="isActive && showTemplatesButton"
      class="bubble-toggle bubble-toggle-secondary bubble-toggle-templates bubble-toggle-with-tooltip"
      type="button"
      :aria-label="$t('bubbleToggle.openTemplates')"
      :data-tooltip="$t('bubbleToggle.openTemplates')"
      @click.stop="onTemplatesClick"
    >
      <svg
        class="bubble-toggle-icon bubble-toggle-icon-document"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          d="M7 2.5h6.8L19.5 8v11.2A2.3 2.3 0 0 1 17.2 21.5H7
          a2.3 2.3 0 0 1-2.3-2.3V4.8A2.3 2.3 0 0 1 7 2.5Zm6.1 1.8v3.2
          c0 .6.4 1 1 1h3.2l-4.2-4.2ZM8.2 11h7.6v1.4H8.2V11Zm0 3.2h7.6v1.4H8.2v-1.4Zm0-6.4h3.2v1.4H8.2V7.8Z"
        />
      </svg>
    </button>

    <button
      v-if="isActive"
      class="bubble-toggle bubble-toggle-secondary bubble-toggle-with-tooltip"
      data-tour-id="samples-register-express-fields-edit-button"
      type="button"
      :aria-label="$t('bubbleToggle.editFields')"
      :data-tooltip="$t('bubbleToggle.editFields')"
      @click.stop="onEditClick"
    >
      <svg
        class="bubble-toggle-icon bubble-toggle-icon-pencil"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          d="m3 17.2 9.9-9.9 3.8 3.8-9.9 9.9L3 21l.2-3.8Zm14.6-9.6
          1.5-1.5a1.9 1.9 0 0 0 0-2.7l-.6-.6a1.9 1.9 0 0 0-2.7 0
          l-1.5 1.5 3.3 3.3Z"
        />
      </svg>
    </button>

    <button
      class="bubble-toggle bubble-toggle-with-tooltip"
      data-tour-id="samples-register-express-toggle-button"
      type="button"
      :class="{ 'is-active': isActive }"
      :aria-pressed="String(isActive)"
      :aria-label="quickActionsLabel"
      :data-tooltip="quickActionsLabel"
      @click="toggle"
    >
      <svg
        class="bubble-toggle-icon"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path d="M13 2 3 14h7l-1 8 10-12h-7l1-8Z" />
      </svg>

      <span
        v-if="showDot && isActive"
        class="bubble-toggle-dot"
        aria-hidden="true"
      />
    </button>
  </div>
</template>

<script>
import { emitSamplesRegisterGa4Event } from '@/services/samplesRegisterAnalytics';

export default {
  name: 'BubbleToggle',
  props: {
    value: { type: Boolean, default: undefined },
    defaultActive: { type: Boolean, default: false },
    storageKey: { type: String, default: '' },
    showDot: { type: Boolean, default: true },
    showTemplatesButton: { type: Boolean, default: true },
  },

  data() {
    return {
      internalActive: this.defaultActive,
    };
  },

  computed: {
    controlled() {
      return this.value !== undefined;
    },
    isActive() {
      return this.controlled ? this.value : this.internalActive;
    },
    quickActionsLabel() {
      return this.isActive
        ? this.$t('bubbleToggle.hideQuickActions')
        : this.$t('bubbleToggle.showQuickActions');
    },
  },

  mounted() {
    if (!this.controlled && this.storageKey) {
      try {
        const raw = localStorage.getItem(this.storageKey);
        if (raw !== null)
          this.internalActive = JSON.parse(raw) === true;
      } catch (error) {
        this.internalActive = false;
        console.warn(
          'Unable to restore express register bubble toggle state',
          error
        );
      }
    }

    this.$emit('toggle', this.isActive);
    this.$emit(this.isActive ? 'activated' : 'deactivated');
  },

  methods: {
    onTemplatesClick() {
      emitSamplesRegisterGa4Event(
        this,
        'sampling_templates_list_open',
        {
          screen_name: 'TemplatesListPage',
          entry_point: 'samples_register_bubble_toggle',
        }
      );
      this.$f7router.navigate(
        {
          name: 'TemplatesListPage',
        },
        {
          animate: true,
          transition: 'f7-fade',
        }
      );
    },

    onEditClick() {
      this.$emit('edit-click');
    },

    toggle() {
      const next = !this.isActive;

      if (!this.controlled) {
        this.internalActive = next;
        if (this.storageKey) {
          try {
            localStorage.setItem(
              this.storageKey,
              JSON.stringify(next)
            );
          } catch (e) {
            console.error(
              this.$t('bubbleToggle.localStorageSaveError'),
              e
            );
          }
        }
      }

      this.$emit('input', next);
      this.$emit('toggle', next);
      this.$emit(next ? 'activated' : 'deactivated');
    },
  },
};
</script>

<style scoped>
.bubble-toggle-group {
  position: fixed;
  right: calc(18px + env(safe-area-inset-right));
  bottom: calc(18px + env(safe-area-inset-bottom));
  z-index: 9999;
}
.bubble-toggle {
  position: relative;
  width: 56px;
  height: 56px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(135deg, #1f6aa5, #3f8cc8);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  line-height: 0;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.2);
}
.bubble-toggle-icon {
  display: block;
  width: 24px;
  height: 24px;
  color: inherit;
  fill: currentColor;
}
.bubble-toggle-icon path {
  fill: currentColor;
}
.bubble-toggle-icon-pencil {
  position: relative;
  width: 18px;
  height: 18px;
}
.bubble-toggle-icon-document {
  width: 18px;
  height: 18px;
}
.bubble-toggle:hover .bubble-toggle-icon {
  transition: transform 0.25s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.bubble-toggle-secondary {
  position: absolute;
  right: 4px;
  bottom: 66px;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #1f6aa5, #3f8cc8);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.2);
}

.bubble-toggle-templates {
  bottom: 114px;
  background: linear-gradient(135deg, #1f6aa5, #3f8cc8);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.2);
}

.bubble-toggle-secondary:hover {
  transform: translateY(-3px) scale(1.04);
  box-shadow: 0 14px 26px rgba(0, 0, 0, 0.24);
}

.bubble-toggle:hover {
  transform: translateY(-4px) scale(1.05);
  box-shadow: 0 18px 35px rgba(0, 0, 0, 0.28);
}

.bubble-toggle:active {
  transform: translateY(-1px) scale(0.97);
}

.bubble-toggle-dot {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #33d17a;
  box-shadow: 0 0 0 2px rgba(51, 209, 122, 0.25);
  animation: pulse 1.8s ease-out infinite;
}

.bubble-toggle-with-tooltip::before,
.bubble-toggle-with-tooltip::after {
  position: absolute;
  top: 50%;
  left: -10px;
  pointer-events: none;
  opacity: 0;
  transform: translate(-10px, -50%) scale(0.96);
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.bubble-toggle-with-tooltip::before {
  content: attr(data-tooltip);
  z-index: 2;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1px;
  color: #f6fbff;
  white-space: nowrap;
  background: rgba(12, 23, 37, 0.86);
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.28);
  backdrop-filter: blur(7px);
  -webkit-backdrop-filter: blur(7px);
}

.bubble-toggle-with-tooltip::after {
  content: '';
  z-index: 1;
  width: 9px;
  height: 9px;
  margin-left: -5px;
  background: rgba(12, 23, 37, 0.86);
  border-right: 1px solid rgba(255, 255, 255, 0.18);
  border-bottom: 1px solid rgba(255, 255, 255, 0.18);
  transform: translate(-10px, -50%) rotate(-45deg);
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.bubble-toggle-with-tooltip:hover::before,
.bubble-toggle-with-tooltip:hover::after,
.bubble-toggle-with-tooltip:focus-visible::before,
.bubble-toggle-with-tooltip:focus-visible::after {
  opacity: 1;
  transform: translate(-100%, -50%) scale(1);
}

.bubble-toggle-with-tooltip:hover::after,
.bubble-toggle-with-tooltip:focus-visible::after {
  transform: translate(-6px, -50%) rotate(-45deg);
}

.bubble-toggle.is-active {
  background: linear-gradient(135deg, #0a66c2, #0dcaf0);
  box-shadow: 0 0 0 4px rgba(13, 202, 240, 0.15),
    0 18px 35px rgba(0, 0, 0, 0.28);
  animation: breathe 2.5s ease-in-out infinite;
}

@keyframes breathe {
  0%,
  100% {
    box-shadow: 0 0 0 4px rgba(13, 202, 240, 0.15),
      0 18px 35px rgba(0, 0, 0, 0.28);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(13, 202, 240, 0.08),
      0 20px 40px rgba(0, 0, 0, 0.32);
  }
}

@keyframes pulse {
  0% {
    transform: scale(0.85);
    opacity: 0.9;
  }
  100% {
    transform: scale(1.8);
    opacity: 0;
  }
}
</style>
